import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import type { LoginResponse } from './auth.service';
import { readSession, writeSession, clearSession, type StoredSession } from './session-storage';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

type SessionRequest = InternalAxiosRequestConfig & { _retry?: boolean; _sessionId?: string };
const REFRESH_TIMEOUT = 15000;
const isAuthRequest = (url?: string) => /\/auth\//.test(url ?? '');

class HttpClient {
  private instance: AxiosInstance;
  private refreshFlight: { sessionId: string; promise: Promise<StoredSession> } | null = null;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.instance.interceptors.request.use((config: SessionRequest) => {
      const session = readSession();
      if (config._retry && config._sessionId !== session?.sessionId) {
        throw new axios.CanceledError('Сессия изменилась');
      }
      if (session && !isAuthRequest(config.url)) {
        config.headers.Authorization = `Bearer ${session.token}`;
        config._sessionId = session.sessionId;
      } else {
        config.headers.delete('Authorization');
      }
      return config;
    });

    this.instance.interceptors.response.use(response => {
      const request = response.config as SessionRequest;
      if (request._sessionId && request._sessionId !== readSession()?.sessionId) {
        throw new axios.CanceledError('Сессия изменилась');
      }
      return response;
    }, async (error: AxiosError) => {
      const request = error.config as SessionRequest | undefined;
      if (error.response?.status !== 401 || !request || request._retry || isAuthRequest(request.url)) {
        throw error;
      }
      const session = readSession();
      if (!session || !request._sessionId || session.sessionId !== request._sessionId) throw error;

      // Каждый запрос, включая ожидающие обновления, повторяется не более одного раза.
      request._retry = true;
      if (request.headers.Authorization === `Bearer ${session.token}`) {
        await this.refreshSession(session);
      }
      // Поздний 401 повторяется с уже обновлённым токеном, без новой ротации.
      return this.instance(request);
    });
  }

  private refreshSession(session: StoredSession): Promise<StoredSession> {
    if (this.refreshFlight?.sessionId === session.sessionId) return this.refreshFlight.promise;
    const flight = {
      sessionId: session.sessionId,
      promise: this.withRefreshLock(() => this.renewSession(session)).finally(() => {
        if (this.refreshFlight === flight) this.refreshFlight = null;
      }),
    };
    this.refreshFlight = flight;
    return flight.promise;
  }

  private async withRefreshLock<T>(operation: () => Promise<T>): Promise<T> {
    if (typeof navigator === 'undefined' || !navigator.locks) return operation();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT);
    try {
      return await navigator.locks.request(`four-mal:refresh:${API_BASE_URL}`, { signal: controller.signal }, () => {
        clearTimeout(timer);
        return operation();
      });
    } finally {
      clearTimeout(timer);
    }
  }

  private async renewSession(expected: StoredSession): Promise<StoredSession> {
    let current = readSession();
    if (!current || current.sessionId !== expected.sessionId) throw new axios.CanceledError('Сессия изменилась');
    if (current.token !== expected.token || current.refreshToken !== expected.refreshToken) return current;

    try {
      const response = await axios.post<ApiResponse<LoginResponse>>(
        `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
        { token: expected.token, refreshToken: expected.refreshToken },
        { timeout: REFRESH_TIMEOUT, headers: { 'Content-Type': 'application/json' } }
      );
      const data = response.data.data;
      if (!response.data.success || !data?.token || !data.refreshToken || !data.userInfo) {
        throw new Error('Некорректный ответ при обновлении сессии');
      }
      current = readSession();
      if (!current || current.sessionId !== expected.sessionId) throw new axios.CanceledError('Сессия изменилась');
      if (current.token !== expected.token || current.refreshToken !== expected.refreshToken) return current;
      return writeSession(data, expected.sessionId);
    } catch (error) {
      current = readSession();
      if (current?.sessionId === expected.sessionId &&
          (current.token !== expected.token || current.refreshToken !== expected.refreshToken)) return current;
      // Только окончательный отказ сервера завершает именно ту сессию, которую обновляли.
      if (axios.isAxiosError(error) && error.response?.status === 401 &&
          current?.sessionId === expected.sessionId) clearSession();
      throw error;
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return (await this.instance.get<ApiResponse<T>>(url, config)).data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return (await this.instance.post<ApiResponse<T>>(url, data, config)).data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return (await this.instance.put<ApiResponse<T>>(url, data, config)).data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return (await this.instance.delete<ApiResponse<T>>(url, config)).data;
  }
}

export const httpClient = new HttpClient();
